import { useState, useEffect } from "react";
import { apiClient } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { AlertTriangle, MapPin, Info } from "lucide-react";
import { useI18n } from "@/context/I18nContext";
import { SearchForm } from "@/components/SearchForm";
import { HomeStats } from "@/components/HomeStats";
import { RecentlyFlagged } from "@/components/RecentlyFlagged";

export default function HomePage() {
  const { t } = useI18n();
  const [stateSources, setStateSources] = useState([]);
  const [facilities, setFacilities] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    apiClient.get("/state-sources").then(r => setStateSources(r.data)).catch(() => {});
    apiClient.get("/facilities").then(r => setFacilities(r.data || [])).catch(() => {}).finally(() => setLoading(false));
  }, []);

  return (
    <div className="relative">
      {/* Hero */}
      <section className="relative overflow-hidden border-b border-slate-200 bg-gradient-to-b from-white to-slate-50 grain">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 py-10 sm:py-16 relative">
          <div className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-3 py-1 font-mono text-[11px] uppercase tracking-widest text-slate-600">
            <span className="inline-block h-1.5 w-1.5 rounded-full bg-emerald-500" />
            {t("home.badge")}
          </div>
          <h1 data-testid="hero-title" className="mt-4 text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight text-slate-900 font-heading leading-tight max-w-3xl">
            {t("home.title")}
          </h1>
          <p className="mt-4 max-w-2xl text-base sm:text-lg text-slate-700 leading-relaxed">
            {t("home.subtitle")}
          </p>

          <div className="mt-4 flex items-start gap-2 rounded-md border border-blue-200 bg-blue-50 p-3 max-w-2xl" data-testid="home-locator-note">
            <Info size={16} className="text-blue-700 mt-0.5 shrink-0" />
            <p className="text-xs text-slate-700 leading-relaxed">
              {t("home.locatorNote")}
            </p>
          </div>

          <div className="mt-4 flex flex-wrap items-center gap-2 rounded-md border-2 border-amber-200 bg-amber-50 p-3 max-w-2xl" data-testid="home-tracker-cta">
            <span className="inline-flex items-center justify-center h-6 w-6 rounded-full bg-amber-500 text-white text-xs font-bold">$2</span>
            <p className="flex-1 text-xs text-slate-800 leading-relaxed">
              <strong>Never miss a lockdown.</strong> Track an inmate for $2/month and get instant email alerts on status changes & transfers.
            </p>
            <a href="/tracker" data-testid="home-tracker-link" className="text-xs font-semibold text-amber-900 hover:underline whitespace-nowrap">Start tracking →</a>
          </div>

          <div className="mt-8">
            <SearchForm />
          </div>
        </div>
      </section>

      <HomeStats facilities={facilities} jurisdictions={stateSources.length} loading={loading} />
      <RecentlyFlagged facilities={facilities} loading={loading} />

      {/* Info cards */}
      <section className="mx-auto max-w-6xl px-4 sm:px-6 py-10 sm:py-14 grid gap-4 md:grid-cols-3">
        <Card className="p-5 border-slate-200">
          <MapPin className="text-blue-600" size={20} />
          <h3 className="mt-3 font-heading font-bold text-lg text-slate-900">{t("home.cardProfilesTitle")}</h3>
          <p className="mt-2 text-sm text-slate-600 leading-relaxed">{t("home.cardProfilesBody")}</p>
        </Card>
        <Card className="p-5 border-slate-200">
          <AlertTriangle className="text-amber-600" size={20} />
          <h3 className="mt-3 font-heading font-bold text-lg text-slate-900">{t("home.cardStatusTitle")}</h3>
          <p className="mt-2 text-sm text-slate-600 leading-relaxed">{t("home.cardStatusBody")}</p>
        </Card>
        <Card className="p-5 border-slate-200">
          <Info className="text-emerald-600" size={20} />
          <h3 className="mt-3 font-heading font-bold text-lg text-slate-900">{t("home.cardVerifyTitle")}</h3>
          <p className="mt-2 text-sm text-slate-600 leading-relaxed">{t("home.cardVerifyBody")}</p>
        </Card>
      </section>

      {/* Coverage list */}
      <section className="mx-auto max-w-6xl px-4 sm:px-6 pb-12">
        <h2 className="font-heading text-2xl font-bold text-slate-900">{t("home.coverageTitle")}</h2>
        <p className="mt-1 text-sm text-slate-600">{t("home.coverageBody")}</p>
        <div className="mt-4 grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
          {stateSources.map((s) => (
            <a
              key={s.state}
              href={s.locator_url}
              target="_blank"
              rel="noreferrer"
              className="flex items-center justify-between rounded-md border border-slate-200 bg-white p-3 hover:border-slate-400 hover:bg-slate-50"
              data-testid={`state-source-${s.code}`}
            >
              <div>
                <div className="font-medium text-slate-900">{s.state}</div>
                <div className="font-mono text-[11px] uppercase tracking-widest text-slate-500">{s.available ? t("home.officialAvailable") : t("home.officialUnavailable")}</div>
              </div>
              <span className="text-xs text-blue-600">{t("home.open")} →</span>
            </a>
          ))}
        </div>
      </section>
    </div>
  );
}
